import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const checkingAuth = React.useRef(false);

  const checkAuth = async () => {
    if (checkingAuth.current) {
      return;
    }

    checkingAuth.current = true;
    setIsLoading(true);

    // Check if we have a session (cookie is set by backend, or session_id in localStorage)
    const hasSession = localStorage.getItem('session_id') || 
                       document.cookie.includes('session_id=');

    if (!hasSession) {
      setIsAuthenticated(false);
      setIsLoading(false);
      checkingAuth.current = false;
      return;
    }

    try {
      // Validate session by making a lightweight API call
      // This will return 401 if session is invalid/expired
      await apiService.listTenants();
      setIsAuthenticated(true);
    } catch (error: any) {
      // If 401 or 403, session is invalid - clear it and logout
      // API Gateway authorizer returns 403 for denied requests
      if (error?.status === 401 || error?.status === 403) {
        console.log('Session invalid, logging out');
        localStorage.removeItem('session_id');
        setIsAuthenticated(false);
      } else {
        console.error('Auth check error:', error);
        // For other errors, assume authenticated (network issues, etc.)
        setIsAuthenticated(true);
      }
    } finally {
      setIsLoading(false);
      checkingAuth.current = false;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

